import { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { identifyDragFactors } from '../../utils/thresholdAnalysis';

// ─── Data loading ─────────────────────────────────────────────────────────────

async function loadThresholdData(deals) {
  if (!deals.length) return { rubricQuestions: [], submissions: [], rubric: null };

  // Fetch pif_submissions for all deals in parallel
  const submissionPromises = deals.map(async deal => {
    const snap = await getDocs(
      query(
        collection(db, 'deals', deal.id, 'pif_submissions'),
        where('is_current', '==', true)
      )
    );
    if (snap.empty) return null;
    const sub = { id: snap.docs[0].id, dealId: deal.id, ...snap.docs[0].data() };
    return { deal, submission: sub };
  });
  const pairs = (await Promise.all(submissionPromises)).filter(Boolean);

  if (!pairs.length) return { rubricQuestions: [], submissions: pairs, rubric: null };

  // Fetch the rubric that matches the most common rubric_id in submissions
  // (per spec 3.4: use rubric version matching the submission)
  const rubricId = pairs[0].submission.rubric_id;
  let rubric = null;
  if (rubricId) {
    const rubricSnap = await getDocs(
      query(collection(db, 'rubrics'), where('__name__', '==', rubricId))
    );
    if (!rubricSnap.empty) {
      rubric = { id: rubricSnap.docs[0].id, ...rubricSnap.docs[0].data() };
    }
  }

  return { rubric, submissions: pairs };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ThresholdSensitivityView({ deals, activeFundId }) {
  const [rubric, setRubric] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hypothetical, setHypothetical] = useState(null);
  const [sortCol, setSortCol] = useState('score');
  const [sortDir, setSortDir] = useState('asc');
  const [expandedDealId, setExpandedDealId] = useState(null);

  useEffect(() => {
    if (!deals.length) { setLoading(false); return; }
    setLoading(true);
    loadThresholdData(deals)
      .then(({ rubric: r, submissions: s }) => {
        setRubric(r);
        setSubmissions(s);
        // Default slider to the rubric's real threshold.
        // Fall back to half of normalisation_factor (or 5.0) when
        // rubric is unavailable — seed/legacy deals have rubric_id: null.
        const normFallback = parseFloat(r?.normalisation_factor ?? 10);
        setHypothetical(
          r?.advancement_threshold != null
            ? parseFloat(r.advancement_threshold)
            : parseFloat((normFallback / 2).toFixed(1))
        );
        setLoading(false);
      })
      .catch(err => {
        console.error('Threshold data load error:', err);
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFundId]);

  if (loading) {
    return <p className="text-muted" style={{ fontSize: 13 }}>Loading threshold data…</p>;
  }

  if (!submissions.length) {
    return (
      <p className="text-muted" style={{ fontSize: 13 }}>
        No PIF submissions found. Submit a PIF to use this view.
      </p>
    );
  }

  if (hypothetical === null) {
    return <p className="text-muted" style={{ fontSize: 13 }}>Loading threshold configuration…</p>;
  }

  const realThreshold = parseFloat(rubric?.advancement_threshold ?? hypothetical);
  const normFactor = parseFloat(rubric?.normalisation_factor ?? 10);
  const rubricQuestions = rubric?.questions || [];

  // ── Build enriched rows ──────────────────────────────────────────────────
  const rows = submissions.map(({ deal, submission }) => {
    const score = parseFloat(submission.normalised_score ?? 0);
    const realPass = score >= realThreshold;
    const simPass = score >= hypothetical;
    const changed = realPass !== simPass;
    const wouldNowPass = !realPass && simPass;
    const wouldNowFail = realPass && !simPass;
    const nearBoundary = Math.abs(score - hypothetical) <= 1.0;

    // Drag factors — only compute for deals below hypothetical threshold
    let dragFactors = [];
    if (!simPass && submission.responses?.length && rubricQuestions.length) {
      dragFactors = identifyDragFactors(submission.responses, rubricQuestions);
    }

    return {
      dealId: deal.id,
      companyName: deal.company_name,
      score,
      realPass,
      simPass,
      changed,
      wouldNowPass,
      wouldNowFail,
      nearBoundary,
      dragFactors,
    };
  });

  // Summary counts
  const wouldNowPassCount = rows.filter(r => r.wouldNowPass).length;
  const wouldNowFailCount = rows.filter(r => r.wouldNowFail).length;
  const unchangedCount = rows.filter(r => !r.changed).length;

  // Sorting
  const sorted = [...rows].sort((a, b) => {
    let av = a[sortCol === 'score' ? 'score' : sortCol];
    let bv = b[sortCol === 'score' ? 'score' : sortCol];
    if (sortCol === 'company') { av = a.companyName; bv = b.companyName; }
    if (typeof av === 'string') return sortDir === 'asc'
      ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'score' ? 'asc' : 'asc'); }
  }
  const th = (col, label) => (
    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(col)}>
      {label} {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  // ── Row background ───────────────────────────────────────────────────────
  function rowStyle(row) {
    if (row.wouldNowPass) return { background: 'rgba(34,197,94,0.08)' };
    if (row.wouldNowFail) return { background: 'rgba(239,68,68,0.08)' };
    return {};
  }
  function rowLeftBorder(row) {
    if (!row.nearBoundary) return {};
    return { borderLeft: '3px solid #c9952a' };
  }

  return (
    <div>
      {/* Warn when no rubric was found — drag factors and real threshold unavailable */}
      {!rubric && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, borderRadius: 6, fontSize: 13,
          background: 'rgba(201,149,42,0.1)', border: '1px solid rgba(201,149,42,0.4)',
          color: 'var(--color-text)',
        }}>
          No rubric configuration found for this fund's submissions. Threshold simulation is available
          using a default value — drag factor analysis requires rubric data and will not appear.
        </div>
      )}

      {/* ── Threshold control ── */}
      <div className="card mb-4">
        <div className="card-body">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 2 }}>
                Current threshold
              </p>
              <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text)' }}>
                {realThreshold.toFixed(1)}
              </p>
            </div>
            <div style={{ flex: 1, minWidth: 260 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 8 }}>
                Simulate threshold:
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="range"
                  min="0"
                  max={normFactor}
                  step="0.1"
                  value={hypothetical}
                  onChange={e => setHypothetical(parseFloat(e.target.value))}
                  style={{ flex: 1 }}
                />
                <input
                  type="number"
                  min="0"
                  max={normFactor}
                  step="0.1"
                  value={hypothetical}
                  onChange={e => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v) && v >= 0 && v <= normFactor) setHypothetical(v);
                  }}
                  style={{
                    width: 72, padding: '4px 8px', borderRadius: 6,
                    border: '1px solid var(--color-border)',
                    fontSize: 14, textAlign: 'center',
                  }}
                />
              </div>
            </div>
            {/* Impact badges */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {wouldNowPassCount > 0 && (
                <span style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                  background: 'rgba(34,197,94,0.12)', color: '#15803d',
                }}>
                  ↑ {wouldNowPassCount} would now PASS
                </span>
              )}
              {wouldNowFailCount > 0 && (
                <span style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                  background: 'rgba(239,68,68,0.12)', color: '#dc2626',
                }}>
                  ↓ {wouldNowFailCount} would now FAIL
                </span>
              )}
              <span style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 13,
                background: 'var(--color-border-light)', color: 'var(--color-text-secondary)',
              }}>
                {unchangedCount} unchanged
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Deal table ── */}
      <div className="card">
        <div className="card-header"><h2>Deal Outcomes</h2></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {th('company', 'Company')}
                {th('score', 'Score')}
                <th>Real Outcome</th>
                <th>Simulated Outcome</th>
                <th>Change</th>
                <th>Primary Drag Factor</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(row => (
                <>
                  <tr
                    key={row.dealId}
                    style={{ ...rowStyle(row), ...rowLeftBorder(row), cursor: row.dragFactors.length > 0 ? 'pointer' : 'default' }}
                    onClick={() => row.dragFactors.length > 0 && setExpandedDealId(
                      expandedDealId === row.dealId ? null : row.dealId
                    )}
                  >
                    <td style={{ fontWeight: 500 }}>
                      {row.companyName}
                      {row.nearBoundary && (
                        <span style={{ fontSize: 10, marginLeft: 6, color: '#c9952a', fontWeight: 600 }}>
                          BOUNDARY
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong>{row.score.toFixed(2)}</strong>
                        <div style={{
                          width: 60, height: 6, background: 'var(--color-border)',
                          borderRadius: 3, overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${Math.min(100, (row.score / normFactor) * 100)}%`,
                            height: '100%',
                            background: row.simPass ? 'var(--color-success)' : 'var(--color-error)',
                            borderRadius: 3,
                          }} />
                        </div>
                      </div>
                    </td>
                    <td>
                      {row.realPass
                        ? <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>✓ Passes</span>
                        : <span style={{ color: 'var(--color-error)' }}>✗ Below threshold</span>}
                    </td>
                    <td>
                      {row.simPass
                        ? <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>✓ Passes</span>
                        : <span style={{ color: 'var(--color-error)' }}>✗ Below threshold</span>}
                    </td>
                    <td>
                      {row.wouldNowPass && (
                        <span style={{ color: '#15803d', fontWeight: 600 }}>↑ Would now PASS</span>
                      )}
                      {row.wouldNowFail && (
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>↓ Would now FAIL</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {row.dragFactors.length > 0 ? (
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                          {row.dragFactors[0].question_text.slice(0, 45)}
                          {row.dragFactors[0].question_text.length > 45 ? '…' : ''}
                          {row.dragFactors.length > 0 && (
                            <span style={{ marginLeft: 4, color: 'var(--color-accent)' }}>
                              {expandedDealId === row.dealId ? '▲' : '▼'}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>

                  {/* Expanded drag factor panel */}
                  {expandedDealId === row.dealId && row.dragFactors.length > 0 && (
                    <tr key={`${row.dealId}-expanded`}>
                      <td colSpan={6} style={{ background: 'var(--color-border-light)', padding: '12px 20px' }}>
                        <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: 'var(--color-text-secondary)' }}>
                          Top drag factors — questions with the largest gap between score achieved and maximum possible:
                        </p>
                        {row.dragFactors.map((df, i) => (
                          <div key={df.question_id} style={{
                            marginBottom: i < row.dragFactors.length - 1 ? 10 : 0,
                            paddingBottom: i < row.dragFactors.length - 1 ? 10 : 0,
                            borderBottom: i < row.dragFactors.length - 1 ? '1px solid var(--color-border)' : 'none',
                          }}>
                            <p style={{ fontSize: 13, fontWeight: 500 }}>
                              #{i + 1}: {df.question_text}
                            </p>
                            <div style={{ display: 'flex', gap: 24, marginTop: 4, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                              <span>Scored: <strong>{df.actual_weighted.toFixed(2)}</strong></span>
                              <span>Max possible: <strong>{df.max_possible_weighted.toFixed(2)}</strong></span>
                              <span>Gap: <strong style={{ color: 'var(--color-error)' }}>
                                −{df.gap.toFixed(2)}
                              </strong></span>
                              <span>({df.gap_percentage.toFixed(0)}% of max)</span>
                            </div>
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', padding: '8px 16px 12px', margin: 0 }}>
          Rows with an amber left border are within ±1.0 of the simulated threshold (boundary zone).
          Click a row to expand drag factors. No data is written to Firestore during simulation.
        </p>
      </div>
    </div>
  );
}
